import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthChange, AuthChangeEvent, useAuthStatus } from './hooks';
import { Flows, AuthenticatorType } from '../lib/allauth';
import { useEffect, useRef } from 'react';
import { identifyUser, resetUser, updateUserProfile } from '../lib/mixpanel';
import {
  useUserProfileData,
  useClearUserProfile,
} from '../hooks/useUserProfileData';
import { getApiBaseUrl } from '@/lib/config';
import { logger } from '@/lib/logger';

export const URLs = Object.freeze({
  LOGIN_URL: '/account/login',
  LOGOUT_URL: '/account/logout',
  LOGIN_REDIRECT_URL: '/',
  LOGOUT_REDIRECT_URL: '/',
});

const flow2path = {};
flow2path[Flows.LOGIN] = '/account/login';
flow2path[Flows.LOGIN_BY_CODE] = '/account/login/code/confirm';
flow2path[Flows.SIGNUP] = '/account/signup';
flow2path[Flows.VERIFY_EMAIL] = '/account/verify-email';
flow2path[Flows.PROVIDER_SIGNUP] = '/account/provider/signup';
flow2path[Flows.REAUTHENTICATE] = '/account/reauthenticate';
flow2path[`${Flows.MFA_AUTHENTICATE}:${AuthenticatorType.TOTP}`] =
  '/account/login/otp';
flow2path[`${Flows.MFA_AUTHENTICATE}:${AuthenticatorType.RECOVERY_CODES}`] =
  '/account/login/otp';
// todo: for now only support totp
// flow2path[`${Flows.MFA_AUTHENTICATE}:${AuthenticatorType.TOTP}`] = '/account/authenticate/totp'
// flow2path[`${Flows.MFA_AUTHENTICATE}:${AuthenticatorType.RECOVERY_CODES}`] = '/account/authenticate/recovery-codes'
flow2path[`${Flows.MFA_AUTHENTICATE}:${AuthenticatorType.WEBAUTHN}`] =
  '/account/authenticate/webauthn';
flow2path[`${Flows.MFA_REAUTHENTICATE}:${AuthenticatorType.TOTP}`] =
  '/account/reauthenticate/totp';
flow2path[`${Flows.MFA_REAUTHENTICATE}:${AuthenticatorType.RECOVERY_CODES}`] =
  '/account/reauthenticate/recovery-codes';
flow2path[`${Flows.MFA_REAUTHENTICATE}:${AuthenticatorType.WEBAUTHN}`] =
  '/account/reauthenticate/webauthn';
flow2path[Flows.MFA_WEBAUTHN_SIGNUP] = '/account/signup/passkey/create';

export function pathForFlow(flow, typ) {
  let key = flow.id;
  if (typeof flow.types !== 'undefined') {
    typ = typ ?? flow.types[0];
    key = `${key}:${typ}`;
  }
  const path = flow2path[key] ?? flow2path[flow.id];
  if (!path) {
    throw new Error(`Unknown path for flow: ${flow.id}`);
  }
  return path;
}

export function pathForPendingFlow(auth) {
  const flow = auth?.data?.flows?.find(flow => flow.is_pending);
  if (flow) {
    return pathForFlow(flow);
  }
  return null;
}

// The origins a backend `next` is allowed to point at: this app's own origin (local
// dev, where /api/ is proxied to Django) and the configured API origin (staging and
// production serve the SPA and Django from different hosts). Both are build-time
// constants, so the allowlist can never grow to an attacker-controlled origin.
function trustedBackendOrigins() {
  const origins = new Set([window.location.origin]);
  const apiBaseUrl = getApiBaseUrl();
  if (apiBaseUrl) {
    origins.add(new URL(apiBaseUrl, window.location.origin).origin);
  }
  return origins;
}

// After login, a `next` that points at a Django-served backend URL (under /api/) has
// no SPA route, so react-router navigate() would render the ErrorPage — and in
// staging/production Django is on a different origin entirely, so the URL has to be
// loaded in full rather than resolved against the SPA origin. Deliver those with a
// full page load; keep client-side navigation for in-SPA paths. Anything that is
// neither falls back to '/', so this can never become an open redirect.
export function navigateAfterAuth(navigate, next) {
  let url;
  try {
    url = new URL(next || '/', window.location.origin);
  } catch {
    navigate('/');
    return;
  }
  if (
    url.pathname.startsWith('/api/') &&
    trustedBackendOrigins().has(url.origin)
  ) {
    window.location.assign(url.toString());
    return;
  }
  if (url.origin === window.location.origin) {
    navigate(url.pathname + url.search + url.hash);
    return;
  }
  navigate('/');
}

function navigateToPendingFlow(auth) {
  const path = pathForPendingFlow(auth);
  if (path) {
    return <Navigate to={path} />;
  }
  return null;
}

export function AuthenticatedRoute({ children }) {
  const location = useLocation();
  const [, status] = useAuthStatus();
  const next = `next=${encodeURIComponent(location.pathname + location.search)}`;
  if (status.isAuthenticated) {
    return children;
  } else {
    return <Navigate to={`${URLs.LOGIN_URL}?${next}`} />;
  }
}

export function AnonymousRoute({ children }) {
  /**
   * A route component that only allows unauthenticated users to access its children.
   * If a user is authenticated, they will be redirected to the login redirect URL.
   */
  const [, status] = useAuthStatus();
  if (!status.isAuthenticated) {
    return children;
  } else {
    return <Navigate to={URLs.LOGIN_REDIRECT_URL} />;
  }
}

export function AuthChangeRedirector({ children }) {
  const [auth, event] = useAuthChange();
  const [, status] = useAuthStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const processedEventsRef = useRef(new Set());
  // Only fetch profile data when authenticated to avoid 401 errors
  const { data: profileData } = useUserProfileData({
    enabled: status.isAuthenticated,
  });
  const userProfile = profileData?.user;
  const clearProfile = useClearUserProfile();

  // Update Mixpanel with profile data when it becomes available
  useEffect(() => {
    if (userProfile) {
      updateUserProfile(userProfile);
    }
  }, [userProfile]);

  useEffect(() => {
    // Only process new events
    if (event && !processedEventsRef.current.has(event)) {
      // Mark this event as handled
      processedEventsRef.current.add(event);

      switch (event) {
        case AuthChangeEvent.LOGGED_OUT:
          // Reset Mixpanel state
          resetUser();
          // Clear cached profile data to prevent stale data on next login
          clearProfile();
          navigate(URLs.LOGOUT_REDIRECT_URL);
          break;
        case AuthChangeEvent.LOGGED_IN: {
          // Identify user in Mixpanel (profile data updated separately when loaded)
          const user = auth.data?.user;
          if (user?.id && user?.email) {
            identifyUser(user.id, user.email);
          }
          const next =
            new URLSearchParams(location.search).get('next') ||
            URLs.LOGIN_REDIRECT_URL;
          navigateAfterAuth(navigate, next);
          break;
        }
        case AuthChangeEvent.REAUTHENTICATED: {
          const next = new URLSearchParams(location.search).get('next') || '/';
          navigateAfterAuth(navigate, next);
          break;
        }
        case AuthChangeEvent.REAUTHENTICATION_REQUIRED: {
          const flow = auth?.data?.flows?.[0];
          if (flow) {
            const next = `next=${encodeURIComponent(location.pathname + location.search)}`;
            const path = pathForFlow(flow);
            navigate(`${path}?${next}`, {
              state: { reauth: auth },
            });
          }
          break;
        }
        case AuthChangeEvent.FLOW_UPDATED: {
          const path = pathForPendingFlow(auth);
          if (!path) {
            logger.error(
              'FLOW_UPDATED event fired but no pending flow path found',
              new Error('No pending flow found'),
              {
                auth_status: auth?.meta?.is_authenticated,
                has_flows: !!auth?.data?.flows,
                flow_count: auth?.data?.flows?.length ?? 0,
              }
            );
            navigate(URLs.LOGIN_URL);
          } else {
            navigate(path);
          }
          break;
        }
        default:
          break;
      }
    } else if (!event && !auth?.meta?.is_authenticated) {
      const path = pathForPendingFlow(auth);
      if (path && path !== location.pathname) {
        // Don't redirect away from the login page — it's the root auth page and
        // users must always be able to return to it (e.g. "Back to password sign in"
        // from the sign-in code flow).
        if (location.pathname === URLs.LOGIN_URL) {
          // User intentionally navigated back to login; don't force them into the pending flow.
        } else if (!location.pathname.startsWith(path + '/')) {
          // Only redirect if we're not already on a more specific version of the flow path.
          // This prevents e.g. /account/verify-email/<key> from redirecting to /account/verify-email/
          logger.debug('Resuming flow', {
            pending_path: path,
            current_path: location.pathname,
          });
          navigate(path);
        }
      }
    }
  }, [event, auth, location, navigate, clearProfile]);

  return children;
}
